import React from 'react';
import { Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  DataGridPro,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridPaginationModel,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useNavigate } from 'react-router-dom';
import { PagedList } from '../../../data/models/paged-list.model';
import { Passenger } from '../../../data/models/passenger.model';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { phoneUtils } from '../../../services/phoneUtils';
import { DateTime } from 'luxon';

const PREFIX = 'SearchResults';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    justifyContent: 'stretch',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },

  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
    '& .MuiDataGrid-row:hover': {
      cursor: 'pointer',
    },
  },
}));

interface SearchResultsProps {
  itemList: PagedList<Passenger>;
  pageSize: number;
  loading: boolean;
  onPageChange: (val: number) => void;
  onPageSizeChange: (val: number) => void;
}

export default function SearchResults({
  itemList,
  pageSize,
  loading,
  onPageChange,
  onPageSizeChange,
}: SearchResultsProps) {
  const t = useCommonTranslation();
  const navigate = useNavigate();

  const renderClientPassengerID = (
    params: GridCellParams,
  ): React.ReactElement => {
    let id = 'ss';
    if (params.row.clients?.length) {
      id = params.row.clients[0].clientPassengerID;
    }
    return <>{id}</>;
  };

  const renderPhoneNumber = (params: GridCellParams): React.ReactElement => {
    let ph = '';
    if (params.row.passengerPhones?.length) {
      ph = phoneUtils.formatPhone(params.row.passengerPhones[0]);
    }
    return <>{ph}</>;
  };

  const renderBirthDate = (params: GridCellParams): React.ReactElement => {
    let bd = '';
    if (params.row.dateOfBirth) {
      bd = DateTime.fromISO(params.row.dateOfBirth).toLocaleString(
        DateTime.DATE_SHORT,
      );
    }
    return <>{bd}</>;
  };

  const renderState = (params: GridCellParams): React.ReactElement => {
    let st = '';
    if (params.row.passengerAddresses?.length) {
      st = params.row.passengerAddresses[0].stateCode;
    }
    return <>{st}</>;
  };

  const columns: GridColDef[] = [
    {
      field: 'passengerUUID',
      headerName: t('passenger.clientId'),
      width: 120,
      sortable: false,
      renderCell: renderClientPassengerID,
    },
    {
      field: 'lastName',
      headerName: t('passenger.lastName'),
      // flex: 1,
      sortable: false,
    },
    {
      field: 'firstName',
      headerName: t('passenger.firstName'),
      // flex: 1,
      sortable: false,
    },
    {
      field: 'phoneNumber',
      headerName: t('passenger.phoneNumber'),
      width: 150,
      sortable: false,
      renderCell: renderPhoneNumber,
    },
    {
      field: 'dateOfBirth',
      headerName: t('passenger.dob'),
      width: 130,
      sortable: false,
      renderCell: renderBirthDate,
    },
    {
      field: 'state',
      headerName: t('passenger.state'),
      width: 60,
      sortable: false,
      renderCell: renderState,
    },
  ];

  const handleRowClick = (params: GridRowParams) => {
    const id = encodeURIComponent(params.id);
    navigate(`/passengers/${id}`);
  };

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== itemList.currentPage - 1) {
      onPageChange(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      onPageSizeChange(model.pageSize);
    }
  };

  return (
    <StyledPaper className={classes.container}>
      <DataGridPro
        getRowId={(row) => row.passengerUUID}
        rows={itemList.items}
        columns={columns}
        pagination={true}
        paginationModel={{ page: itemList.currentPage - 1, pageSize }}
        rowCount={itemList.itemCount}
        autoHeight={true}
        loading={loading}
        paginationMode="server"
        sortingMode="server"
        className={classes.grid}
        checkboxSelection={false}
        disableColumnMenu={true}
        disableColumnSelector={true}
        pageSizeOptions={[10, 20, 30, 40, 50, 100]}
        onPaginationModelChange={paginationChange}
        onRowClick={handleRowClick}
      />
    </StyledPaper>
  );
}
